/* @typescript-eslint/restrict-template-expressions */
import { Box, Checkbox, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, styled, tableCellClasses } from '@mui/material'
import Pagination from '../../../../components/TablePagination'
import React, { useEffect, useRef } from 'react'
import { visuallyHidden } from '@mui/utils'
import { type UpdateGroupRequest, type Asset, type AdvancedFilterPostRequest, type ConjuctionOptions, type GroupsAssetObj } from '../../types'
import LanguageStrings from '../../../../i18n/locales'
import SearchBox from '../../../../components/SearchBox'
import { type UnassignedAssetListState } from '../../redux/UnassignedAssetListSlice'
import { type RootState } from '../../../../store'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { type ConnectedProps, connect } from 'react-redux'
import { advancedAssignedAssetModifyFilter, fetchUnassignedAssetList, updateGroup } from '../../redux/actionCreator'
import { getTemplateModal } from '../createGroupComponents/utils/getTemplateModal'
import { Button } from '../../../../components/Button'
import { type AdvFilterParameterConditions } from './AssignedAssetList'
import AdvancedFilter from '../../../../components/AdvancedFilterComponents/AdvancedFilter'
import dayjs from 'dayjs'
import Clearfilter from '../../../../assets/Clearfilter.svg'
import _ from 'lodash'
import CheckboxDropdown, { type CheckboxDropdownMode } from '../../../../components/CheckboxDropdown'

const AssetAssignedStrings = LanguageStrings().AssetGroupStrings.GroupDetailsComponent.AssignedAssetList

type Order = 'asc' | 'desc'

interface Sort {
  order: Order
  orderBy: string
}

const style = {
  width: '99%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  maxHeight: 'calc(90vh - 176px)',
  overflowY: 'auto'
}
interface EnhancedTableProps {
  dataList: GroupsAssetObj[]
  order: Order
  orderBy: string
  onRequestSort: (event: React.MouseEvent<unknown>, newOrderBy: keyof Asset) => void
  selectedOption: CheckboxDropdownMode
  setSelectedOption: React.Dispatch<React.SetStateAction<CheckboxDropdownMode>>
  handleSelectionModeChange: (prevMode: CheckboxDropdownMode, newMode: CheckboxDropdownMode) => void
  currPageAssets: string[]
  currPageSelectedAssets: string[]
  containerRef: React.RefObject<HTMLDivElement>
}

function EnhancedTableHead (props: EnhancedTableProps): any {
  const { order, orderBy, onRequestSort, dataList, currPageAssets, selectedOption, setSelectedOption, handleSelectionModeChange, currPageSelectedAssets, containerRef } = props
  const createSortHandler =
        (newOrderBy: keyof Asset) => (event: React.MouseEvent<unknown>) => {
          onRequestSort(event, newOrderBy)
        }
  return (dataList !== null && dataList !== undefined && <>
    <TableHead data-testid='table-header'>
        <TableRow>
          <StyledTableCell padding="checkbox"style={{ borderRight: 'none', paddingLeft: '8px' }}>
            <CheckboxDropdown containerRef={containerRef} onSelectionModeChange={handleSelectionModeChange} selectedOption={selectedOption} setSelectedOption={setSelectedOption}
                            currPageTotal={currPageAssets.length} currPageSelectedAssets={currPageSelectedAssets} dataTestId={'UnassignedAssets-Checkboxdropdown'} />
          </StyledTableCell>
          {headCells.map((headCell: HeadCell, idx: number) => (
              <StyledTableCell
                  key={headCell.id}
                  align='left'
                  padding={headCell.disablePadding ? 'none' : 'normal'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  style={{ borderLeft: idx === 0 ? 'none' : 'inherit' }}
              >
                <TableSortLabel
                    hideSortIcon={!headCell.sortable}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
                    aria-label={headCell.label}
                    data-testid="test-sort"
                >
                    {headCell.label}
                    {orderBy === headCell.id
                      ? (
                        // istanbul ignore next
                        <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                        )
                      : null}
                </TableSortLabel>
              </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    </>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap',
    lineHeight: '20px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'assetId',
    numeric: false,
    disablePadding: false,
    label: AssetAssignedStrings.AssetId,
    sortable: true
  },
  {
    id: 'serialNumber',
    numeric: false,
    disablePadding: false,
    label: AssetAssignedStrings.SerialNumber,
    sortable: true
  },
  {
    id: 'assetType',
    numeric: false,
    disablePadding: false,
    label: AssetAssignedStrings.AssetType,
    sortable: false
  },
  {
    id: 'state',
    numeric: false,
    disablePadding: false,
    label: AssetAssignedStrings.State,
    sortable: false
  },
  {
    id: 'firmware_version',
    numeric: false,
    disablePadding: false,
    label: AssetAssignedStrings.FirmwareVersion,
    sortable: false
  },
  {
    id: 'tag',
    numeric: false,
    disablePadding: false,
    label: AssetAssignedStrings.Tag,
    sortable: false
  }
]

const UnassignedAssetListTable = (props: AllProps): JSX.Element => {
  const [unassignedAssetList, setUnassignedAssetList] = React.useState<GroupsAssetObj[]>([])
  const [searchInput, setSearchInput] = React.useState(props.searchInput)
  const [selectedAssets, setSelectedAssets] = React.useState<string[]>(props.addedAssets)
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'asc', orderBy: 'id' })
  const rowsPerPage = 10
  const [showTemplates, setShowTemplates] = React.useState<string[]>([])
  // Advance Filter states
  const [showAdvancedFilter, setShowAdvancedFilter] = React.useState(false)
  const [conditions, setConditions] = React.useState<AdvFilterParameterConditions[]>(props.conditions)
  const [conjuction, setConjuction] = React.useState<ConjuctionOptions>(props.conjuction)
  const [triggerAdvFilter, setTriggerAdvFilter] = React.useState(false)
  // CheckboxDropdown states
  const [selectedOption, setSelectedOption] = React.useState<CheckboxDropdownMode>(props.selectedOption)
  const [unselectedAssets, setUnselectedAssets] = React.useState<string[]>(props.unselectedAssets)
  const [selectedPages, setSelectedPages] = React.useState<number[]>(props.selectedPages)
  const [initialState] = React.useState({ selectedOption: props.selectedOption, selectedAssets: props.addedAssets, unselectedAssets: props.unselectedAssets })
  const [initOp1, setInitOp1] = React.useState<boolean>(false)
  const [initOp2, setInitOp2] = React.useState<boolean>(false)
  const [initOp3, setInitOp3] = React.useState<boolean>(false)
  const [fullyUnselectedPages, setFullyUnselectedPages] = React.useState<number[]>(props.fullyUnselectedPages)
  const checkbpxDropdownContainerRef = useRef<HTMLDivElement>(null)
  const [pagesWithIndividualSelections, setPagesWithIndividualSelections] = React.useState<number[]>(props.pagesWithIndividualSelections)
  const totalPages = (props.unassignedAssetList.unassignedAssetList != null)
    ? Math.ceil(props.unassignedAssetList.unassignedAssetList.total_count / rowsPerPage)
    : 0
  const allPages = Array.from({ length: totalPages }, (_, i) => i + 1)
  let partiallySelectedPages = selectedOption === 'all'
    ? allPages.filter(p => !fullyUnselectedPages.includes(p))
    : Array.from(new Set([...selectedPages, ...pagesWithIndividualSelections]))

  if (unselectedAssets.length > 0 && selectedOption === 'all') {
    const areCurrentPageAssetsUnselected = unassignedAssetList.every((asset: GroupsAssetObj) => unselectedAssets.includes(asset.asset_id))
    if (areCurrentPageAssetsUnselected) {
      partiallySelectedPages = partiallySelectedPages.filter((p) => p !== page)
    }
  }

  const handleSelectionModeChange = (prevMode: CheckboxDropdownMode, newMode: CheckboxDropdownMode): void => {
    const currPageIds = unassignedAssetList.map((asset: GroupsAssetObj) => asset.asset_id)
    if (prevMode === 'none' && newMode === 'all') {
      setSelectedAssets([])
      setUnselectedAssets([])
      setSelectedPages([])
      setPagesWithIndividualSelections([])
      setFullyUnselectedPages([])
    } else if (prevMode === 'none' && newMode === 'current') {
      if (!selectedPages.includes(page)) {
        setSelectedPages([...selectedPages, page])
      }
      setSelectedAssets([...selectedAssets, ...currPageIds.filter((id) => !selectedAssets.includes(id))])
      setUnselectedAssets([])
      setFullyUnselectedPages([])
    } else if (prevMode === 'current' && newMode === 'none') {
      const currPageSelectedIds = currPageIds.filter((id) => selectedAssets.includes(id))
      setSelectedAssets([...selectedAssets.filter((id) => !currPageSelectedIds.includes(id))])
      setUnselectedAssets([])
      setSelectedPages(selectedPages.filter((p) => p !== page))
      setFullyUnselectedPages([])
    } else if (prevMode === 'current' && newMode === 'all') {
      setSelectedAssets([])
      setUnselectedAssets([])
      setSelectedPages([])
      setFullyUnselectedPages([])
    } else if (prevMode === 'all' && newMode === 'none') {
      setSelectedAssets([])
      setUnselectedAssets([])
      setSelectedPages([])
      setPagesWithIndividualSelections([])
      setFullyUnselectedPages([])
    } else {
      setSelectedAssets(currPageIds)
      setUnselectedAssets([])
      setSelectedPages([page])
      setPagesWithIndividualSelections([page])
      setFullyUnselectedPages([])
    }
  }

  useEffect(() => {
    const currentPageAssetIds = unassignedAssetList.map((asset) => asset.asset_id)
    const hasSelections = currentPageAssetIds.some((id) => selectedAssets.includes(id))
    if (hasSelections && !pagesWithIndividualSelections.includes(page)) {
      setPagesWithIndividualSelections([...pagesWithIndividualSelections, page])
    } else if (!hasSelections && !selectedPages.includes(page)) {
      setPagesWithIndividualSelections(pagesWithIndividualSelections.filter((p) => p !== page))
    }
  }, [unassignedAssetList, selectedAssets, selectedPages, page])

  // istanbul ignore next
  useEffect(() => {
    if (initOp3) {
      setSelectedAssets([])
      setUnselectedAssets([])
      setSelectedOption('none')
      setSelectedPages([])
      setPagesWithIndividualSelections([])
      setFullyUnselectedPages([])
    }
    setInitOp3(true)
  }, [sort])

  useEffect(() => {
    if (initOp1) {
      const currentPageAssetIds = unassignedAssetList.map((asset: GroupsAssetObj) => asset.asset_id)
      const selectedAssetsOnCurrentPage = currentPageAssetIds.filter((id) => selectedAssets.includes(id))
      if (selectedOption === 'current') {
        if (selectedAssetsOnCurrentPage.length === 0 || selectedAssetsOnCurrentPage.length < currentPageAssetIds.length) {
          setSelectedOption('none')
        }
      } else if (selectedOption === 'none' && selectedAssetsOnCurrentPage.length === currentPageAssetIds.length && currentPageAssetIds.length > 0) {
        setSelectedOption('current')
      } else if (selectedOption === 'all' && props.unassignedAssetList.unassignedAssetList?.total_count !== undefined && unselectedAssets.length === props.unassignedAssetList.unassignedAssetList?.total_count) {
        setSelectedOption('none')
      }
    }
    setInitOp1(true)
  }, [page, sort, selectedAssets, unassignedAssetList, selectedOption, unselectedAssets])

  // istanbul ignore next
  const resetFilters = (): void => {
    setConditions([])
    setTriggerAdvFilter(prev => !prev)
  }

  // istanbul ignore next
  const openAdvancedFilter = (): void => {
    setShowAdvancedFilter(true) // Show the modal
  }

  /* istanbul ignore next */
  const handleRowClick = (id: string): void => {
    const currentPageAssetIds = unassignedAssetList.map((asset: GroupsAssetObj) => asset.asset_id)
    if (selectedOption === 'current') {
      if (selectedAssets.includes(id)) {
        setSelectedAssets(selectedAssets.filter((assetId) => assetId !== id))
      } else {
        setSelectedAssets([...selectedAssets, id])
      }
    } else if (selectedOption === 'all') {
      let newUnselectedAssets: string[] = []
      if (unselectedAssets.includes(id)) {
        newUnselectedAssets = unselectedAssets.filter((assetId) => assetId !== id)
        setUnselectedAssets(newUnselectedAssets)
      } else {
        newUnselectedAssets = [...unselectedAssets, id]
        setUnselectedAssets(newUnselectedAssets)
      }
      const allUnselected = currentPageAssetIds.every((assetId) => newUnselectedAssets.includes(assetId))
      if (allUnselected) {
        setFullyUnselectedPages((prev) => [...new Set([...prev, page])])
      } else {
        setFullyUnselectedPages((prev) => prev.filter((p) => p !== page))
      }
    } else {
      if (selectedAssets.includes(id)) {
        setSelectedAssets(selectedAssets.filter((assetId) => assetId !== id))
      } else {
        setSelectedAssets([...selectedAssets, id])
      }
    }
    const selectedAssetsOnCurrentPage = currentPageAssetIds.filter((id) => selectedAssets.includes(id))
    if (selectedOption === 'none' && selectedAssetsOnCurrentPage.length === currentPageAssetIds.length) {
      setSelectedOption('current')
      if (!selectedPages.includes(page)) {
        setSelectedPages([...selectedPages, page])
      }
    }
  }

  // istanbul ignore next
  const handleAdvanceFilter = (page: number, groupId: string): void => {
    setSearchInput('')
    const advancedFilterPostRequest: AdvancedFilterPostRequest = {
      conditions: conditions.map(cond => {
        let rowVal: string | string[] = cond.value
        if (cond.operator === 'Between') {
          if (cond.dataType === 'time') {
            rowVal = [dayjs(cond.value[0]).format('HH:mm:ss'), dayjs(cond.value[1]).format('HH:mm:ss')]
          } else {
            rowVal = [cond.value[0], cond.value[1]]
          }
        } else {
          if (cond.dataType === 'time') {
            rowVal = dayjs(cond.value[0]).format('HH:mm:ss')
          } else {
            if (typeof cond.value === 'object') {
              rowVal = cond.value[0]
            } else {
              rowVal = cond.value
            }
          }
        }
        return {
          key: cond.parameter,
          operation: cond.operator,
          value: rowVal,
          datatype: cond.dataType
        }
      }),
      conjuction
    }

    props.advancedFilter(advancedFilterPostRequest, page - 1, rowsPerPage, groupId, sort.order, sort.orderBy, 'ASSET_LIST_GROUP_MODIFY_PAGE', 'asset')
  }

  const handleFetchUnassignedAssetList = (): void => {
    let groupId = sessionStorage.getItem('selectedGroupId')
    // istanbul ignore next
    if (groupId === null) {
      groupId = ''
    }

    if (conditions.length > 0) {
      handleAdvanceFilter(page, groupId)
    } else {
      page >= 1 && props.fetchUnassignedAssetList(page - 1, rowsPerPage, sort.order, sort.orderBy, groupId, 'unassigned', props.assetTypeId, searchInput)
    }
  }

  const debouncedHandleFetchUnassignedAssetList = _.debounce(handleFetchUnassignedAssetList, 500)

  useEffect(() => {
    debouncedHandleFetchUnassignedAssetList()
  }, [page, sort])

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    } else {
      debouncedHandleFetchUnassignedAssetList()
    }
    if (initOp2) {
      setSelectedAssets([])
      setUnselectedAssets([])
      setSelectedOption('none')
      setSelectedPages([])
      setFullyUnselectedPages([])
      setPagesWithIndividualSelections([])
    }
    setInitOp2(true)
  }, [searchInput, triggerAdvFilter])

  // istanbul ignore next
  const handleRequestSort =
        (event: React.MouseEvent<unknown>, newOrderBy: keyof Asset): void => {
          const isAsc = sort.orderBy === newOrderBy && sort.order === 'asc'
          const toggledOrder = isAsc ? 'desc' : 'asc'
          setSort({ order: toggledOrder, orderBy: newOrderBy })
        }

  useEffect(() => {
    if (props.unassignedAssetList.unassignedAssetList !== null && props.unassignedAssetList.httpStatus === 200) {
      setUnassignedAssetList(props.unassignedAssetList.unassignedAssetList.group_details)
    }
  }, [props.unassignedAssetList.unassignedAssetList])

  const handleAddClick = (): void => {
    props.setAddedAssets(selectedAssets)
    props.setUnselectedAssets(unselectedAssets)
    props.setSearchInput(searchInput)
    props.setConditions(conditions)
    props.setConjunction(conjuction)
    props.setIsAdvanceFilterSelected(conditions.length > 0)
    props.setSelectedPages(selectedPages)
    props.setSelectedOption(selectedOption)
    props.setShowAddAssetWindow(false)
    props.setPagesWithIndividualSelections(pagesWithIndividualSelections)
    props.setFullyUnselectedPages(fullyUnselectedPages)
  }

  function didStatesHaveChanged (): boolean {
    return !_.isEqual(initialState, { selectedOption, selectedAssets, unselectedAssets })
  }

  const isSelected = (id: string): boolean => (selectedOption === 'current' && selectedAssets.includes(id)) || (selectedOption === 'all' && !unselectedAssets.includes(id)) || (selectedOption === 'none' && selectedAssets.includes(id))

  const getAssetTableWithFailure = (): JSX.Element => {
    // istanbul ignore else
    if (props.unassignedAssetList.unassignedAssetList !== null && props.unassignedAssetList.unassignedAssetList.group_details.length > 0 && props.unassignedAssetList.httpStatus === 200) {
      return (
        <>
          <Box sx={style} className="devices">
            <Paper
              sx={{
                width: '100%',
                mb: 2,
                backgroundColor: '#272727',
                boxShadow: 'unset',
                color: '#F0F0F0',
                marginBottom: '0'
              }}
            >
              <TableContainer className="device-table">
                <Table
                  sx={{ minWidth: 750, color: '#f0f0f0' }}
                  aria-labelledby="tableTitle"
                  size={'small'}
                >
                  <EnhancedTableHead
                    containerRef={checkbpxDropdownContainerRef}
                    order={sort.order}
                    orderBy={sort.orderBy}
                    onRequestSort={handleRequestSort}
                    dataList={unassignedAssetList}
                    selectedOption={selectedOption}
                    currPageSelectedAssets={unassignedAssetList.filter((asset) => selectedAssets.includes(asset.asset_id)).map((asset: GroupsAssetObj) => asset.asset_id)}
                    setSelectedOption={setSelectedOption}
                    handleSelectionModeChange={handleSelectionModeChange}
                    currPageAssets={unassignedAssetList.map((asset: GroupsAssetObj) => asset.asset_id)}
                  />
                  <TableBody>
                    {unassignedAssetList.length > 0 &&
                      unassignedAssetList.map((row: GroupsAssetObj, index: number) => {
                        const isItemSelected = isSelected(row.asset_id)
                        const labelId = `enhanced-table-checkbox-${row.asset_id}`
                        return (
                          <StyledTableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.asset_id}
                            aria-label="groups-list-row"
                            selected={isItemSelected}
                            sx={{ borderLeft: '2px solid #202020', borderRight: '1px solid #202020' }}
                          >
                            <StyledTableCell className='responsive-font' padding="checkbox">
                                <Checkbox
                                    sx={{ color: '#909090' }}
                                    checked={isItemSelected}
                                    onChange={
                                      // istanbul ignore next
                                      () => { handleRowClick(row.asset_id) }}
                                    inputProps={{
                                      'aria-labelledby': labelId,
                                      'aria-label': 'test-checkbox',
                                      id: `select-item-${index}`
                                    }}
                                />
                            </StyledTableCell>
                            <StyledTableCell className='responsive-font'
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="none"
                              aria-label="group-name"
                              data-testid="group-name"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '25vw'
                              }}
                            >
                              {row.asset_id}
                            </StyledTableCell>
                            <StyledTableCell className='responsive-font'>
                              {row.serial_number}
                            </StyledTableCell>
                            <StyledTableCell className='responsive-font'>
                              {row.asset_type}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.state}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.firmware_version}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row?.tag?.length > 0 && row.tag[0]}
                            </StyledTableCell>
                          </StyledTableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              {props.unassignedAssetList.unassignedAssetList.total_count > 10 && (<Pagination selectedPages={partiallySelectedPages} count={props.unassignedAssetList.unassignedAssetList.total_count} page={page} setPage={setPage} rowsPerPage={rowsPerPage} siblingCount={1}
                          boundaryCount={1} />)}
            </Paper>
          </Box>
          {
          showTemplates.length > 0
            ? getTemplateModal(showTemplates, () => { setShowTemplates([]) }, 'Configurations', 'CloseTemplatePopup')
            : <></>
          }
          <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
            <Button sx={{ margin: 10 }} dataTestId='unassigned-add-btn' onClick={handleAddClick} disabled={!didStatesHaveChanged()} primaryColor={didStatesHaveChanged() ? undefined : '#B0B0B0'}>{AssetAssignedStrings.Add}</Button>
          </div>
        </>
      )
    } else if (props.unassignedAssetList.isLoading) {
      return (
        <div className='CircularprogressAmi'>
          <CircularProgress />
        </div>
      )
    } else if (props.unassignedAssetList.httpStatus === 401) {
      return (
              <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (props.unassignedAssetList.httpStatus === 403) {
      return (
          <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
      )
    } else if (props.unassignedAssetList.httpStatus === 404) {
      return <div className="authError"><AuthError errorMessage="NoDataPresent" /></div>
    } else if (((props.unassignedAssetList.unassignedAssetList === null || props.unassignedAssetList.unassignedAssetList.group_details.length === 0) && props.unassignedAssetList.httpStatus === 200) || (props.unassignedAssetList.httpStatus === 404 && props.unassignedAssetList.error === 'No Assets Found')) {
      return (
          <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (props.unassignedAssetList.httpStatus === 400) {
      return <div className="authError"><AuthError errorMessage="invalidSearchInput" /></div>
    } else {
      return (
          <div className='authError'><AuthError errorMessage="cannotFetch" retry={handleFetchUnassignedAssetList}/></div>
      )
    }
  }

  return (
    <>
      <div style={{ flexDirection: 'row', display: 'flex' }}>
      <Button
          type='hollow'
          dataTestId='advanced-filter-btn'
          width='200px'
          height='35px'
          onClick={openAdvancedFilter}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginLeft: '13px' }}>{AssetAssignedStrings.AdvancedFilter} ({conditions.length})</div>
          <span onClick={(e) => {
            e.stopPropagation()
            resetFilters()
          }}>
            <img src={Clearfilter} alt='clear filter' style={{ marginLeft: '25px', width: '18px', paddingTop: '5px' }} />
            </span>
          </div>
        </Button>
        {conditions.length === 0
          ? <SearchBox sx={{ width: '420px' }}
            immediate={true}
            debounce={true}
            placeholder={AssetAssignedStrings.SearchPlaceHolder}
            emptyEnterFallback={() => { return undefined }}
            searchVal={searchInput} setSearchVal={setSearchInput}
            performAction={() => { return undefined }}
        />
          : <></>}
      </div>
      {getAssetTableWithFailure()}
      { showAdvancedFilter && <AdvancedFilter
        conditions={conditions}
        setConditions={setConditions}
        conjuction={conjuction}
        setConjuction={setConjuction}
        setPageSize={setPage}
        setShowAdvancedFilter= {setShowAdvancedFilter}
        triggerAdvFilter={triggerAdvFilter}
        setTriggerAdvFilter={setTriggerAdvFilter}
        searchKey='asset'
        disableOverflow={false}
      />}
    </>
  )
}

interface DispatchToProps {
  updateGroup: (groupId: string, updateGroupRequest: UpdateGroupRequest) => void
  fetchUnassignedAssetList: (page: number, size: number, sortdir: string, sortfield: string, groupId: string, input: string, assetTypeId: string, searchInput: string) => void
  advancedFilter: (advancedFilterPostRequest: AdvancedFilterPostRequest, page: number, size: number, groupId: string, sortdir: string, sortfield: string, searchPage: string, searchKey: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  updateGroup: (groupId: string, updateGroupRequest: UpdateGroupRequest) => dispatch(updateGroup(groupId, updateGroupRequest)),
  fetchUnassignedAssetList: (page: number, size: number, sortdir: string, sortfield: string, groupId: string, input: string, assetTypeId: string, searchInput: string) => dispatch(fetchUnassignedAssetList(page, size, sortdir, sortfield, groupId, input, assetTypeId, searchInput)),
  advancedFilter: (advancedFilterPostRequest: AdvancedFilterPostRequest, page: number, size: number, groupId: string, sortdir: string, sortfield: string, searchPage: string, searchKey: string) =>
    dispatch(advancedAssignedAssetModifyFilter(advancedFilterPostRequest, page, size, groupId, sortdir, sortfield, searchPage, searchKey))
})

interface StateToProps {
  unassignedAssetList: UnassignedAssetListState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  unassignedAssetList: state.UnassignedAssetList
})

const connector = connect(mapStateToProps, mapDispatchToProps)
interface ParentProps {
  assetTypeId: string
  addedAssets: string[]
  setAddedAssets: React.Dispatch<React.SetStateAction<string[]>>
  setShowAddAssetWindow: React.Dispatch<React.SetStateAction<boolean>>
  unselectedAssets: string[]
  setUnselectedAssets: React.Dispatch<React.SetStateAction<string[]>>
  searchInput: string
  setSearchInput: React.Dispatch<React.SetStateAction<string>>
  selectedOption: CheckboxDropdownMode
  setSelectedOption: React.Dispatch<React.SetStateAction<CheckboxDropdownMode>>
  selectedPages: number[]
  setSelectedPages: React.Dispatch<React.SetStateAction<number[]>>
  pagesWithIndividualSelections: number[]
  setPagesWithIndividualSelections: React.Dispatch<React.SetStateAction<number[]>>
  fullyUnselectedPages: number[]
  setFullyUnselectedPages: React.Dispatch<React.SetStateAction<number[]>>
  conditions: AdvFilterParameterConditions[]
  setConditions: React.Dispatch<React.SetStateAction<AdvFilterParameterConditions[]>>
  conjuction: ConjuctionOptions
  setConjunction: React.Dispatch<React.SetStateAction<ConjuctionOptions>>
  setIsAdvanceFilterSelected: React.Dispatch<React.SetStateAction<boolean>>
}
type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = ParentProps & PropsFromRedux

export default connector(UnassignedAssetListTable)
